import { useCallback, useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useOrgFeatureFlags } from '../../hooks/useOrgFeatureFlags';
import { useOrganizations } from '../../contexts/OrganizationContext';
import { apiService } from '../../services/api';
import { brandDirectoryApi } from '../../services/brandDirectoryApi';
import { hasStoreProductQaAccess } from '../../utils/productQaAccess';
import {
  navTrees,
  profileItem,
  NavGroup,
  NavLeaf,
  ShellRole,
  DIRECTORY_PATH,
  DIRECTORY_SIBLING_PATHS,
} from './navConfig';
import { deriveTopBarTitle, pickActiveEntry } from './navTitle';
import { isNavLeafVisible } from './navLeafVisibility';
import { navLeafLockName } from '../planLock/planSurface';
import { usePlanRequirements } from '../planLock/usePlanRequirements';

/** A nav row plus the group it belongs to, which is what the palette shows as its path. */
export interface NavEntry {
  leaf: NavLeaf;
  /** i18n key of the containing group, or null for an ungrouped root row. */
  groupLabelKey: string | null;
}

export interface NavModel {
  role: ShellRole;
  roots: NavLeaf[];
  groups: NavGroup[];
  /** Every reachable row, flattened, in sidebar order. Backs the command palette. */
  entries: NavEntry[];
  activeLeaf: NavLeaf | null;
  /** The group holding the active row — the breadcrumb's only content. */
  activeGroup: NavGroup | null;
  /** Section label key for the breadcrumb: the active group, or the root row itself. */
  sectionLabelKey: string | null;
  isActive: (leaf: NavLeaf) => boolean;
  badgeFor: (leaf: NavLeaf) => number;
  /** Cheapest plan name when the row is shown locked; null when it navigates as usual. */
  lockName: (leaf: NavLeaf) => string | null;
}

const PINS_STORAGE_KEY = 'nav-favorites';

export const loadPinnedPaths = (): string[] => {
  if (typeof window === 'undefined') return [];
  const saved = window.localStorage.getItem(PINS_STORAGE_KEY);
  if (!saved) return [];
  try {
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed.filter((path) => typeof path === 'string') : [];
  } catch {
    return [];
  }
};

export const savePinnedPaths = (paths: string[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(paths));
};

export const useNavModel = (): NavModel => {
  const location = useLocation();
  const { userProfile, isAdmin: authIsAdmin } = useAuth();
  const { getEffectiveRole, getEffectiveUserType, isImpersonating } = useImpersonation();
  const { isEnabled: isOrgFlagEnabled, isOnUnlessOff: isUiSwitchOn, isLoading: flagsLoading, sourceOf } = useOrgFeatureFlags();
  const planReq = usePlanRequirements();
  // The group pages appear only for someone who manages one.
  const { managesGroup } = useOrganizations();

  const effectiveRole = getEffectiveRole(userProfile);
  const effectiveUserType = getEffectiveUserType(userProfile);

  const role: ShellRole =
    authIsAdmin && effectiveRole === 'admin'
      ? 'admin'
      : effectiveUserType === 'vendor_supplier'
        ? 'vendor'
        : 'store';

  const isStoreRole = role === 'store';
  const hasProductQaAccess = hasStoreProductQaAccess({
    isImpersonating,
    productQaEnabled: userProfile?.productQaEnabled,
  });

  // Same key as the product browser's organization-selection query (its
  // `selectionIntegrationId` is null when no integration is picked, and the
  // key hashes `null` and `undefined` alike), so loading the products page
  // issues one `GET /store/sync-selection`, not two in parallel, and every
  // `invalidateQueries({ queryKey: ['store-sync-selection'] })` after a
  // selection write refreshes this count too.
  const { data: storeSyncSelection } = useQuery({
    queryKey: ['store-sync-selection', null],
    queryFn: () => apiService.getStoreSyncSelection(),
    enabled: isStoreRole,
    staleTime: 30 * 1000,
  });
  const syncSelectedCount = storeSyncSelection?.selectedProductIds?.length || 0;

  const { data: pendingApprovals } = useQuery({
    queryKey: ['vendor-approval-pending'],
    queryFn: () => brandDirectoryApi.pendingApprovals(),
    enabled: role === 'admin',
    staleTime: 30 * 1000,
  });
  const pendingApprovalCount = pendingApprovals?.length || 0;

  const isPathMatch = useCallback(
    (paths: string[]) => paths.some((path) => !!matchPath({ path, end: true }, location.pathname)),
    [location.pathname],
  );

  const isActive = useCallback(
    (leaf: NavLeaf) => {
      // Directory owns its brand detail pages but not its three sibling routes.
      if (leaf.path === DIRECTORY_PATH) {
        if (
          DIRECTORY_SIBLING_PATHS.some(
            (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
          )
        ) {
          return false;
        }
        return (
          location.pathname === leaf.path ||
          location.pathname.startsWith(`${leaf.path}/brands/`)
        );
      }
      return isPathMatch(leaf.matchPaths?.length ? leaf.matchPaths : [leaf.path]);
    },
    [isPathMatch, location.pathname],
  );

  /**
   * The flag hooks return freshly-created functions each render, so keying
   * the tree on them rebuilt every group object every time. Anything downstream
   * comparing identities then saw constant change. Collapse the flag answers
   * into a primitive instead, and rebuild only when one actually flips.
   */
  const tree = navTrees[role];
  const allLeaves = [...tree.roots, ...tree.groups.flatMap((group) => group.items)];
  // Computed every render, same as the flag signature this replaced: the flag
  // helpers are new closures, and a memo keyed only on loading would keep a
  // row visible after an override lands without a loading flip.
  const lockById = new Map<string, string | null>();
  const flagSignature = allLeaves
    .map((leaf) => {
      const uiSwitch = leaf.uiSwitch ? String(isUiSwitchOn(leaf.uiSwitch)) : '';
      const org = leaf.orgFlag ? String(isOrgFlagEnabled(leaf.orgFlag)) : '';
      const lock = navLeafLockName(leaf, {
        flagsLoading,
        showLocked: planReq.showLocked,
        requirements: planReq.requirements,
        requirementsPending: planReq.requirementsPending,
        requirementsFailed: planReq.requirementsFailed,
        orgFlagEnabled: leaf.orgFlag ? isOrgFlagEnabled(leaf.orgFlag) : false,
        orgFlagSource: leaf.orgFlag ? sourceOf(leaf.orgFlag) : undefined,
      });
      lockById.set(leaf.id, lock);
      return `${leaf.id}:${uiSwitch}:${org}:${lock ?? ''}`;
    })
    .join('|');

  const { roots, groups } = useMemo(() => {
    const isLeafVisible = (leaf: NavLeaf) => isNavLeafVisible(leaf, {
      uiSwitchOn: !leaf.uiSwitch || isUiSwitchOn(leaf.uiSwitch),
      hasProductQa: hasProductQaAccess,
      managesGroup,
      lockName: lockById.get(leaf.id) ?? null,
      orgFlagOn: !leaf.orgFlag || isOrgFlagEnabled(leaf.orgFlag),
    });

    const visibleRoots = tree.roots.filter(isLeafVisible);
    const promotedRoots: NavLeaf[] = [];
    const visibleGroups: NavGroup[] = [];

    tree.groups.forEach((group) => {
      const items = group.items.filter(isLeafVisible);
      if (items.length === 0) return;
      // A group of one is just a link; nesting it produces "TEAM → Team".
      if (items.length === 1) {
        promotedRoots.push({ ...items[0], icon: group.icon });
        return;
      }
      visibleGroups.push({ ...group, items });
    });

    return { roots: [...visibleRoots, ...promotedRoots], groups: visibleGroups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, flagSignature, hasProductQaAccess, managesGroup]);

  const entries = useMemo<NavEntry[]>(() => {
    const list: NavEntry[] = roots.map((leaf) => ({ leaf, groupLabelKey: null }));
    groups.forEach((group) => {
      group.items.forEach((leaf) => list.push({ leaf, groupLabelKey: group.labelKey }));
    });
    list.push({ leaf: profileItem, groupLabelKey: null });
    return list;
  }, [roots, groups]);

  // One most-specific match drives the whole breadcrumb, so group and page can't disagree.
  const activeEntry = useMemo(() => pickActiveEntry(entries, isActive), [entries, isActive]);
  const activeLeaf = activeEntry?.leaf ?? null;
  const activeGroup = useMemo(
    () => groups.find((group) => group.labelKey === activeEntry?.groupLabelKey) ?? null,
    [groups, activeEntry],
  );
  const sectionLabelKey = deriveTopBarTitle(activeEntry)?.pageKey ?? null;

  const badgeFor = useCallback(
    (leaf: NavLeaf) => {
      if (leaf.badgeKey === 'approvals') return pendingApprovalCount;
      if (leaf.badgeKey === 'syncSelection') return isStoreRole ? syncSelectedCount : 0;
      return 0;
    },
    [pendingApprovalCount, syncSelectedCount, isStoreRole],
  );

  return {
    role,
    roots,
    groups,
    entries,
    activeLeaf,
    activeGroup,
    sectionLabelKey,
    isActive,
    badgeFor,
    lockName: (leaf: NavLeaf) => lockById.get(leaf.id) ?? null,
  };
};
